import {
  type CSSProperties,
  type PointerEvent,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import { edgeFadeMask } from '../../styles/edge-fade';
import { cn } from '../../utils/cn';
import {
  MARQUEE_FADE_PX,
  type MarqueeLabelProps,
  PLAY_ONCE_DELAY_MS,
} from './MarqueeLabel.types';

/** Hover dwell before a clamped label starts scrolling. */
const HOVER_DELAY_MS = 500;
/** Marquee pace — px of hidden text revealed per second (slow). */
const MARQUEE_SPEED_PX_S = 24;
/** Motion spans 88% of each cycle (12% far-end hold) — compensate pace. */
const HOLD_COMPENSATION = 1 / 0.88;

/**
 * Pointer types whose enter/leave mean an actual hover. NEVER `'touch'`: a
 * tap fires a synthetic `pointerenter` (and `mouseenter`) with no matching
 * leave until the user happens to tap somewhere else, so a touch-started
 * marquee would loop unattended for as long as the row stays on screen —
 * the "it never stops" failure this gate exists to prevent.
 */
const HOVER_POINTER_TYPES = new Set(['mouse', 'pen']);

/**
 * Web clamped label: truncates to `maxWidth` with an ellipsis, and when the
 * text is ACTUALLY truncated, hovering for ~0.5s turns it into a slow marquee
 * (ping-pong scroll) for as long as the pointer stays. Hover is gated to
 * hover-capable pointers (mouse / pen) — see {@link HOVER_POINTER_TYPES}.
 * Truncation is measured on the rendered DOM (`scrollWidth` vs
 * `clientWidth`) — exact for the loaded webfont, no text-measurement
 * dependency needed.
 *
 * `playOnce` adds the one-shot activation pass (single there-and-back cycle
 * after {@link PLAY_ONCE_DELAY_MS}, then rest) — see the prop docs.
 *
 * Width source:
 *   - `maxWidth` set → clamp to that fixed px width (Breadcrumb crumbs, Tag).
 *   - `maxWidth` omitted → `block w-full`; the parent must constrain the width
 *     (a `min-w-0` flex child — Badge chip labels, DeckCard titles,
 *     MenuListItem labels/descriptions).
 *
 * `overflow="fade"` swaps the ellipsis for a trailing alpha ramp (see the
 * prop docs). The mask is unconditional — it only bites where the text
 * reaches the box's trailing edge, so a label that fits is untouched.
 *
 * Internal machinery shared by Breadcrumb crumbs, Tag labels, Badge chip
 * labels, DeckCard titles, MenuListItem, SidebarMenuItem and SrButton — not
 * a caller-facing component. The native counterpart
 * (`MarqueeLabel.native.tsx`) runs the same marquee on press-and-hold and on
 * pen/trackpad hover.
 */
/** The `fade` clamp's trailing alpha ramp — the DS edge fade. */
const FADE_MASK = edgeFadeMask({ right: true }, MARQUEE_FADE_PX);

interface MarqueeState {
  shift: number;
  duration: number;
  /** A one-shot activation pass — 2 alternate iterations, then rest. */
  once: boolean;
}

export function MarqueeLabel({
  label,
  maxWidth,
  className,
  overflow = 'ellipsis',
  playOnce = false,
  testID,
}: MarqueeLabelProps) {
  const outerRef = useRef<HTMLSpanElement>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const [marquee, setMarquee] = useState<MarqueeState | null>(null);
  // Whether the label is ACTUALLY clipped. The fade ramp only earns its keep
  // when there is something behind it: applied unconditionally it also dims
  // the tail of a title that ends just inside the box ("Biology basics" in a
  // 108px slot), which reads as truncation where none happened — the exact
  // opposite of the "show more of the name" this mode exists for.
  const [clipped, setClipped] = useState(false);
  const fade = overflow === 'fade';
  useLayoutEffect(() => {
    const outer = outerRef.current;
    if (!fade || !outer) return;
    const measure = () => setClipped(outer.scrollWidth - outer.clientWidth > 1);
    measure();
    // Re-measure on reflow: a card title's box changes width with the grid's
    // column count, and a font swap re-measures the text under it.
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(outer);
    return () => observer.disconnect();
  }, [fade, label]);

  // Transforms don't affect scrollWidth, so this measures the full text even
  // while a previous marquee span is mid-animation.
  const startMarquee = (once: boolean) => {
    const outer = outerRef.current;
    if (!outer) return;
    const shift = outer.scrollWidth - outer.clientWidth;
    // Sub-pixel overflow isn't real truncation — don't scroll for it.
    if (shift <= 1) return;
    setMarquee({
      shift,
      duration: Math.max(2, (shift / MARQUEE_SPEED_PX_S) * HOLD_COMPENSATION),
      once,
    });
  };

  const onPointerEnter = (e: PointerEvent<HTMLSpanElement>) => {
    if (!HOVER_POINTER_TYPES.has(e.pointerType)) return;
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => startMarquee(false), HOVER_DELAY_MS);
  };

  const onPointerLeave = () => {
    clearTimeout(timerRef.current);
    // A running activation pass is not hover's to cancel: it is bounded and
    // ends on its own (or when the row deactivates).
    setMarquee((m) => (m?.once ? m : null));
  };

  // The activation pass — see `playOnce` in MarqueeLabel.types. Runs on the
  // rising edge (including mounting active), and the falling edge cancels a
  // pending or mid-flight pass so a deactivated row never keeps moving.
  useEffect(() => {
    if (!playOnce) {
      setMarquee((m) => (m?.once ? null : m));
      return;
    }
    const timer = setTimeout(() => startMarquee(true), PLAY_ONCE_DELAY_MS);
    return () => clearTimeout(timer);
  }, [playOnce]);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  return (
    <span
      ref={outerRef}
      data-testid={testID}
      onPointerEnter={onPointerEnter}
      onPointerLeave={onPointerLeave}
      className={cn(
        'block min-w-0 select-none',
        // `truncate` is overflow-hidden + nowrap + text-overflow:ellipsis;
        // the fade variant wants the first two and NOT the third — the ramp
        // is what signals the clamp, and an `…` under it would just eat a
        // character's worth of the text the fade exists to reveal.
        marquee || fade ? 'overflow-hidden whitespace-nowrap' : 'truncate',
        maxWidth == null && 'w-full',
        className,
      )}
      style={{
        ...(maxWidth != null ? { maxWidth } : null),
        ...(fade && clipped ? FADE_MASK : null),
      }}>
      {marquee ? (
        <span
          className="inline-block whitespace-nowrap animate-marquee"
          // The keyframes ping-pong via `alternate`, so 2 iterations = one
          // there-and-back pass ending at rest — the one-shot's whole cycle.
          onAnimationEnd={marquee.once ? () => setMarquee(null) : undefined}
          style={
            {
              '--marquee-shift': `-${marquee.shift}px`,
              '--marquee-duration': `${marquee.duration}s`,
              ...(marquee.once ? { animationIterationCount: 2 } : null),
            } as CSSProperties
          }>
          {label}
        </span>
      ) : (
        label
      )}
    </span>
  );
}
